// Entrada (P0.6): entrar, criar conta (teste de 7 dias), confirmar e-mail, recuperar
// senha (com a tela "Nova senha" ao voltar do link) e Google (no desktop abre o navegador
// do sistema e volta por beedoit://).
import { useEffect, useState, type FormEvent, type MouseEvent, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { LegalDoc } from '../../domain/legal';
import {
  MIN_PASSWORD,
  isValidEmail,
  tidyName,
  validateNewPassword,
  validateSignIn,
  validateSignUp,
  type AuthFieldError,
} from '../../domain/authValidation';
import { AuthError, type OAuthProvider } from '../../data/auth';
import { useStore } from '../../state/AppStore';
import { useServices } from '../../state/services';
import { BeeLogo, GoogleLogo, Icon, MicrosoftLogo } from '../Icon';
import { LegalDialog } from '../LegalDialog';
import { takeSignUpFirst } from '../../platform/entryParams';

type Screen = 'signIn' | 'signUp' | 'confirm' | 'forgot' | 'forgotSent' | 'browser';

function Field({ id, label, error, children }: { id: string; label: string; error?: string | null; children: ReactNode }) {
  return (
    <div className="auth__field">
      <label htmlFor={id}>{label}</label>
      {children}
      {error && (
        <p className="field-error" id={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

function Shell({ title, sub, children }: { title: string; sub?: string; children: ReactNode }) {
  // versão web: o campo das abelhas pintado no fundo e o cartão branco no centro (AC-257)
  const web = !useServices().desktop;
  return (
    <main className={web ? 'auth auth--scene' : 'auth'} data-testid="auth">
      <div className="auth__card">
        <div className="auth__brand">
          <BeeLogo size={88} />
        </div>
        <h1 className="auth__title">{title}</h1>
        {sub && <p className="auth__sub">{sub}</p>}
        {children}
      </div>
    </main>
  );
}

/** Depois do link de recuperação: a pessoa já está logada e só precisa escolher a senha nova (AC-092). */
export function NewPasswordScreen() {
  const { t } = useTranslation();
  const auth = useStore().backend.auth;
  const [password, setPassword] = useState('');
  const [error, setError] = useState<AuthFieldError | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const v = validateNewPassword(password);
    setError(v);
    if (v) return;
    setBusy(true);
    setFormError(null);
    try {
      await auth.updatePassword(password);
    } catch (err) {
      setFormError(t(`errors.auth.${err instanceof AuthError ? err.code : 'unknown'}`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title={t('auth.newPasswordTitle')} sub={t('auth.newPasswordSub')}>
      <form className="auth__form" onSubmit={save} noValidate>
        <Field id="new-password" label={t('auth.newPassword')} error={error ? t(`errors.auth.${error}`, { min: MIN_PASSWORD }) : null}>
          <input id="new-password" className="input" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {formError && <p className="form-error" role="alert">{formError}</p>}
        <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
          {t('auth.savePassword')}
        </button>
      </form>
    </Shell>
  );
}

export function AuthScreens() {
  const { t } = useTranslation();
  const store = useStore();
  const services = useServices();
  const auth = store.backend.auth;
  // veio do site ("Baixar para Windows" ou um plano): abre em "Criar conta" (AC-257)
  const [screen, setScreen] = useState<Screen>(() => (takeSignUpFirst() ? 'signUp' : 'signIn'));
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<string, AuthFieldError>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** provedor aberto no navegador do sistema (desktop) — a tela de espera diz qual (AC-090) */
  const [provider, setProvider] = useState<OAuthProvider>('google');
  /** Termos ou Privacidade abertos no pop-up, por cima do cadastro (AC-087) */
  const [legal, setLegal] = useState<LegalDoc | null>(null);

  const fieldMsg = (k: string) => (errors[k] ? t(`errors.auth.${errors[k]}`, { min: MIN_PASSWORD }) : null);
  const go = (s: Screen) => {
    setErrors({});
    setFormError(null);
    setScreen(s);
  };

  // a pessoa saiu para abrir o e-mail: ao voltar para a janela, já está em "Entrar" (AC-093)
  useEffect(() => {
    if (screen !== 'confirm') return;
    const back = () => {
      if (document.visibilityState === 'visible') go('signIn');
    };
    window.addEventListener('focus', back);
    document.addEventListener('visibilitychange', back);
    return () => {
      window.removeEventListener('focus', back);
      document.removeEventListener('visibilitychange', back);
    };
  }, [screen]);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setFormError(null);
    try {
      await fn();
    } catch (err) {
      const code = err instanceof AuthError ? err.code : 'unknown';
      setFormError(t(`errors.auth.${code}`));
    } finally {
      setBusy(false);
    }
  };

  const signIn = (e: FormEvent) => {
    e.preventDefault();
    const v = validateSignIn(email, password);
    setErrors(v);
    if (Object.keys(v).length) return;
    void run(async () => {
      await auth.signIn(email.trim(), password);
    });
  };

  const signUp = (e: FormEvent) => {
    e.preventDefault();
    const v = validateSignUp({ name, email, password, acceptTerms: terms });
    setErrors(v);
    if (Object.keys(v).length) return;
    void run(async () => {
      const r = await auth.signUp({ name: tidyName(name), email: email.trim(), password });
      if (r.needsConfirmation) go('confirm');
    });
  };

  const forgot = (e: FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setErrors({ email: email.trim() ? 'invalid_email' : 'required' });
      return;
    }
    void run(async () => {
      await auth.resetPassword(email.trim());
      go('forgotSent');
    });
  };

  const oauth = (p: OAuthProvider) =>
    void run(async () => {
      setProvider(p);
      if (services.desktop) go('browser');
      await auth.signInWithOAuth(p);
    });

  const socialButtons = (
    <>
      <div className="auth__or">
        <span>{t('auth.or')}</span>
      </div>
      <button type="button" className="btn btn--outline btn--block" disabled={busy} onClick={() => oauth('google')}>
        <GoogleLogo />
        {t('auth.google')}
      </button>
      {services.oauthProviders.includes('azure') && (
        <button type="button" className="btn btn--outline btn--block" disabled={busy} onClick={() => oauth('azure')}>
          <MicrosoftLogo />
          {t('auth.microsoft')}
        </button>
      )}
      {/* entrar pelo Google (ou pela Microsoft, quando ativada) pode criar a conta: o aceite fica dito aqui (AC-095) */}
      <p className="auth__legal">
        <Trans
          i18nKey={services.oauthProviders.includes('azure') ? 'auth.socialTerms' : 'auth.socialTermsGoogle'}
          components={{
            terms: <button type="button" className="link-btn" onClick={(e) => openLegal(e, 'terms')} />,
            privacy: <button type="button" className="link-btn" onClick={(e) => openLegal(e, 'privacy')} />,
          }}
        />
      </p>
    </>
  );

  // o link fica dentro da frase da caixa: abre o pop-up sem marcar nem desmarcar a caixa
  const openLegal = (e: MouseEvent, doc: LegalDoc) => {
    e.preventDefault();
    setLegal(doc);
  };

  if (screen === 'confirm') {
    return (
      <Shell title={t('auth.confirmTitle')} sub={t('auth.confirmSub', { email })}>
        <div className="auth__icon">
          <Icon name="mail" size={28} />
        </div>
        <p className="auth__text">{t('auth.confirmBody')}</p>
        <button type="button" className="btn btn--primary btn--block" onClick={() => go('signIn')}>
          {t('auth.backToSignIn')}
        </button>
      </Shell>
    );
  }

  if (screen === 'forgotSent') {
    return (
      <Shell title={t('auth.forgotSentTitle')} sub={t('auth.forgotSent')}>
        <button type="button" className="btn btn--primary btn--block" onClick={() => go('signIn')}>
          {t('auth.backToSignIn')}
        </button>
      </Shell>
    );
  }

  if (screen === 'browser') {
    return (
      <Shell title={t('auth.googleWaitTitle')} sub={provider === 'azure' ? t('auth.microsoftWaitSub') : t('auth.googleWaitSub')}>
        <div className="auth__icon">
          <Icon name="globe" size={28} />
        </div>
        <p className="auth__text">{t('auth.googleWaitBody')}</p>
        <button type="button" className="btn btn--ghost btn--block" onClick={() => go('signIn')}>
          {t('common.cancel')}
        </button>
      </Shell>
    );
  }

  if (screen === 'forgot') {
    return (
      <Shell title={t('auth.forgotTitle')} sub={t('auth.forgotSub')}>
        <form className="auth__form" onSubmit={forgot} noValidate>
          <Field id="email" label={t('auth.email')} error={fieldMsg('email')}>
            <input id="email" className="input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          {formError && <p className="form-error" role="alert">{formError}</p>}
          <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
            {t('auth.sendLink')}
          </button>
          <button type="button" className="link-btn" onClick={() => go('signIn')}>
            {t('auth.backToSignIn')}
          </button>
        </form>
      </Shell>
    );
  }

  if (screen === 'signUp') {
    return (
      <Shell title={t('auth.signUpTitle')}>
        <form className="auth__form" onSubmit={signUp} noValidate>
          <Field id="name" label={t('auth.name')} error={fieldMsg('name')}>
            <input id="name" className="input" autoComplete="given-name" placeholder={t('auth.namePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field id="email" label={t('auth.email')} error={fieldMsg('email')}>
            <input id="email" className="input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field id="password" label={t('auth.password')} error={fieldMsg('password')}>
            <input id="password" className="input" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <label className="auth__check">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
            <span>
              <Trans
                i18nKey="auth.terms"
                components={{
                  terms: <button type="button" className="link-btn" onClick={(e) => openLegal(e, 'terms')} />,
                  privacy: <button type="button" className="link-btn" onClick={(e) => openLegal(e, 'privacy')} />,
                }}
              />
            </span>
          </label>
          {errors.terms && <p className="field-error">{t(`errors.auth.${errors.terms}`)}</p>}
          {formError && <p className="form-error" role="alert">{formError}</p>}
          <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
            {t('auth.createAccount')}
          </button>
          {socialButtons}
          <p className="auth__switch">
            <span>{t('auth.haveAccount')}</span>
            <button type="button" className="link-btn" onClick={() => go('signIn')}>
              {t('auth.signIn')}
            </button>
          </p>
        </form>
        {legal && <LegalDialog doc={legal} onClose={() => setLegal(null)} />}
      </Shell>
    );
  }

  return (
    <Shell title={t('auth.signInTitle')} sub={t('auth.signInSub')}>
      <form className="auth__form" onSubmit={signIn} noValidate>
        <Field id="email" label={t('auth.email')} error={fieldMsg('email')}>
          <input id="email" className="input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field id="password" label={t('auth.password')} error={fieldMsg('password')}>
          <input id="password" className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <button type="button" className="link-btn auth__forgot" onClick={() => go('forgot')}>
          {t('auth.forgot')}
        </button>
        {formError && <p className="form-error" role="alert">{formError}</p>}
        <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
          {t('auth.signIn')}
        </button>
        {socialButtons}
        <p className="auth__switch">
          <span>{t('auth.noAccount')}</span>
          <button type="button" className="link-btn" onClick={() => go('signUp')}>
            {t('auth.createAccount')}
          </button>
        </p>
      </form>
      {legal && <LegalDialog doc={legal} onClose={() => setLegal(null)} />}
    </Shell>
  );
}
